import React, {useCallback, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {
  AUTH_COUNTRY_DEFAULT,
  REGEX_ALPHABET_AND_SPACE,
  REGEX_PHONE_NUMBER,
} from 'consts'
import {translate} from 'i18n'
import {requestData} from 'services'
import {AuthCountryResponse, parseShape} from 'types'
import {getFontFamily, showSnackbar} from 'utils'
import {
  Button,
  Input,
  InputArea,
  InputPhone,
  Modal,
  Paragraph,
  ParsedText,
} from 'common/components'
import {
  VALIDATION_AUTH_EMAIL,
  VALIDATION_AUTH_PHONE,
  VALIDATION_AUTH_REQUIRED,
  VALIDATION_GIFTSHOP_ADDBANKACCOUNT_FULLNAME,
} from 'common/validations'
import convertUnit from 'lib/unit'
import {PartnershipModalProps} from './PartnershipModalProps'

const StyledButtonsContainer = styled.div`
  display: flex;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
  border-radius: ${convertUnit(16)};
`

const StyledTitle = styled(Paragraph)`
  text-align: center;
`

const StyledDescription = styled(Paragraph)`
  text-align: left;
  white-space: pre-wrap;
  margin-top: ${convertUnit(4)};
  margin-bottom: ${convertUnit(16)};
`

export default function PartnershipModal({stateModal}: PartnershipModalProps) {
  const [modal, setModal] = stateModal
  const handleLoadDataPhoneDummy = useCallback(
    (search: string): AuthCountryResponse[] =>
      !search || 'indonesia'.includes(search.toLowerCase())
        ? [AUTH_COUNTRY_DEFAULT]
        : [],
    [],
  )
  const stateCountry = useState(AUTH_COUNTRY_DEFAULT)
  const country = stateCountry[0]
  const defaultValues = {
    name: '',
    email: '',
    phone_number: '',
    message: '',
  }
  const form = useForm({defaultValues})
  const [loading, setLoading] = useState(false)
  const {errors, watch} = form
  const {name, email, phone_number, message} = watch()

  const handleClickSend = useCallback(() => {
    setLoading(true)
    requestData('giftshop_post_partnership', {
      data: {
        name,
        email,
        phone_code: country.dial_code,
        phone_number,
        message,
      },
      onRequestReceived: () => {
        setLoading(false)
      },
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('giftShop:partnershipSuccess'))
        } else {
          showSnackbar(translate('global:messageError', {context: status}))
        }
        setModal(false)
      },
    })
  }, [country.dial_code, email, message, name, phone_number, setModal])

  const isDisabled = useMemo(
    () =>
      loading ||
      errors.name !== undefined ||
      errors.email !== undefined ||
      errors.phone_number !== undefined ||
      name === '' ||
      email === '' ||
      phone_number === '' ||
      message?.length <= 0,
    [
      email,
      errors.email,
      errors.name,
      errors.phone_number,
      loading,
      message?.length,
      name,
      phone_number,
    ],
  )

  const patterns: parseShape[] = useMemo(() => {
    const regex = /\*\*(.+?)\*\*/
    return [
      {
        pattern: regex,
        style: {fontFamily: getFontFamily('medium')},
        renderText: (text) => text.replace(regex, `$1`),
      },
    ]
  }, [])

  return (
    <StyledModal visible={modal} onBackdropPress={() => setModal(false)}>
      <StyledTitle fontSize="l" fontWeight="bold" lineHeight={31}>
        {translate('giftShop:partnership')}
      </StyledTitle>
      <StyledDescription fontSize="m" color="gray_5" lineHeight={26}>
        <ParsedText parse={patterns}>
          {translate('giftShop:partnershipDesc')}
        </ParsedText>
      </StyledDescription>
      <Input
        name="name"
        form={form}
        value={name}
        containerStyle={{
          marginBottom: convertUnit(20),
        }}
        formRules={VALIDATION_GIFTSHOP_ADDBANKACCOUNT_FULLNAME}
        allowedCharacters={REGEX_ALPHABET_AND_SPACE}
        label={translate('giftShop:partnershipNameLabel')}
        placeholder={translate('giftShop:partnershipNamePlaceholder')}
        rightIcon="delete"
      />
      <Input
        name="email"
        form={form}
        value={email}
        containerStyle={{
          marginBottom: convertUnit(20),
        }}
        formRules={VALIDATION_AUTH_EMAIL}
        label={translate('global:email')}
        placeholder={translate('giftShop:addEmailPlaceholder')}
        rightIcon="delete"
      />
      <InputPhone
        name="phone_number"
        loadData={handleLoadDataPhoneDummy}
        stateSelect={stateCountry}
        form={form}
        formRules={VALIDATION_AUTH_PHONE}
        allowedCharacters={REGEX_PHONE_NUMBER}
        emptyMessage={(search) =>
          translate('auth:registerIdentifierSearchCountryEmpty', {
            search,
          })
        }
        label={translate('global:phoneNumber')}
        placeholder={translate('auth:registerIdentifierInputPhonePlaceholder')}
        maxLength={13}
        searchPlaceholder={translate('global:search')}
        autoComplete={false}
        containerStyle={{marginBottom: convertUnit(20)}}
        rightIcon="delete"
      />
      <InputArea
        name="message"
        form={form}
        value={message}
        containerStyle={{
          marginBottom: convertUnit(20),
        }}
        formRules={VALIDATION_AUTH_REQUIRED}
        label={translate('global:message')}
        placeholder={translate('giftShop:partnershipMessagePlaceholder')}
        maxLength={300}
        inputContainerStyle={{height: convertUnit(100)}}
      />
      <StyledButtonsContainer>
        <StyledButton
          label={translate('global:cancel')}
          color="primary_5"
          backgroundColor="white_3"
          backgroundHoverColor="white_4"
          onClick={() => setModal(false)}
        />
        <StyledButton
          isLoading={loading}
          label={translate('global:send')}
          onClick={handleClickSend}
          disabled={isDisabled}
        />
      </StyledButtonsContainer>
    </StyledModal>
  )
}
